import { useSeo } from '@/lib/seo';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { AlertTriangle, Bell, MapPin, Radio, Shield, Smartphone, Zap, Truck, CalendarClock, Wifi, ArrowUpRight, Lightbulb, Send, Loader2, CheckCircle2, CloudRain, Leaf, Building2, Camera, LayoutGrid, Check } from 'lucide-react';

export default function Landing() {
  useSeo({ description: 'Live New Zealand highway closures, crashes and traffic events plus MetService severe-weather warnings from NZTA Waka Kotahi. Route watch, web push, email & SMS alerts, freight bridge restrictions and oversize load-pilot scheduling.' });
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/alerts/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1647456751346-44ad13e4b5cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxOZXclMjBaZWFsYW5kJTIwaGlnaHdheSUyMHN1bnNldHxlbnwwfHx8fDE3ODMyMzUxNzN8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-40 lg:pb-48">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white text-xs tracking-[0.2em] uppercase font-semibold mb-6" data-testid="hero-badge">
              <span className="w-2 h-2 rounded-full bg-teal-400 pulse-red" />
              Live NZTA + MetService Feeds
            </div>
            <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tight mb-6">
              Never miss a <span className="text-orange-400">road closure</span> again.
            </h1>
            <p className="text-white/85 text-lg lg:text-xl mb-8 max-w-2xl leading-relaxed">
              PTT NZ Alerts is an operations-grade platform that streams real-time NZ highway incidents,
              closures, and MetService severe-weather warnings — snow, wind and heavy rain — straight to
              your phone, inbox, or NOC wall.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/signup" className="btn btn-primary" data-testid="hero-cta-signup">
                Get Started – Free <Zap className="w-4 h-4" />
              </Link>
              <Link to="/alerts" className="btn btn-secondary" data-testid="hero-cta-alerts">
                See Live Alerts
              </Link>
            </div>

            {/* Live stats */}
            {stats && (
              <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl" data-testid="hero-stats">
                <StatMini value={stats.by_severity.red} label="Critical" tint="#E11D48" />
                <StatMini value={stats.by_severity.orange} label="Urgent" tint="#F7941D" />
                <StatMini value={stats.by_severity.yellow} label="Info" tint="#FBBF24" />
                <StatMini value={stats.highways_affected} label="Highways" tint="#2DD4B0" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cross-product bar → Connectivity Portal */}
      <a
        href="https://portal.pttnz.net"
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-neutral-900 hover:bg-neutral-800 transition-colors"
        data-testid="portal-link-bar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-white">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-teal-500/15 text-teal-400 flex items-center justify-center flex-shrink-0">
              <Wifi className="w-5 h-5" />
            </span>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-teal-400 font-semibold">Also from PTT NZ · Connectivity</div>
              <div className="text-sm sm:text-base font-semibold">One SIM for every NZ network — seamless roaming across Spark, One NZ &amp; 2degrees on a single SIM</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-400 whitespace-nowrap">
            Explore One SIM <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </a>

      {/* Features */}
      <section className="py-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-600 mb-3">How it works</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl mb-4">Built for operators.<br />Loved by commuters.</h2>
          <p className="text-zinc-600 text-lg">
            One platform, many trusted feeds — Waka Kotahi NZTA road events (every 2 minutes) and MetService
            severe-weather warnings — with a hand-crafted operations layer for teams that need to act fast.
            More data sources are on the way.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Feature icon={<Radio className="w-6 h-6" />} title="Live NZTA Feed" body="Direct integration with Waka Kotahi's Traffic Road Events service — real-time, verified data." />
          <Feature icon={<CloudRain className="w-6 h-6" />} title="MetService Weather Warnings" body="Official severe-weather watches & warnings — road snowfall, strong wind and heavy rain — mapped by region and colour-coded Yellow/Orange/Red, right alongside road events." />
          <Feature icon={<AlertTriangle className="w-6 h-6" />} title="3-Tier Severity" body="Cumulative alert levels: Critical (closures), Urgent (crashes), Info (roadworks). Choose your threshold." />
          <Feature icon={<Smartphone className="w-6 h-6" />} title="Multi-Channel" body="In-portal, push notifications (PWA + iOS/Android app-ready), email included, SMS as a paid add-on." />
          <Feature icon={<MapPin className="w-6 h-6" />} title="Highway Subscriptions" body="Follow only the highways that matter to you – SH1, SH2, SH20, or all of them." />
          <Feature icon={<Shield className="w-6 h-6" />} title="Admin Broadcasts" body="Operators can manually create alerts and push them to every subscriber instantly." />
          <Feature icon={<Bell className="w-6 h-6" />} title="NOC Wall Display" body="Purpose-built Network Operations Centre screen for wall displays. Never times out." />
          <Feature icon={<CalendarClock className="w-6 h-6" />} title="Load Pilot Scheduling" body="Pilots publish planned oversize loads with waypoint ETAs — crews coordinate head-on meets and transport teams see delays coming." />
          <Feature icon={<Truck className="w-6 h-6" />} title="Freight Restriction Layers" body="NZTA bridge crossings, 50MAX and HPMV (58t) approved routes overlaid on your corridor for oversize-load planning." />
        </div>
      </section>

      {/* Business plan showcase — real live screenshots */}
      <section className="bg-black text-white py-24 px-6 lg:px-12" data-testid="business-showcase">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-teal-400 mb-4">
              <Building2 className="w-4 h-4" /> Business plan
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl leading-tight mb-5">
              A live command centre for your <span className="text-orange-400">operations room</span>
            </h2>
            <p className="text-zinc-400 text-lg">
              Business unlocks the Network Operations Centre — a nationwide live picture plus a wall of
              NZTA CCTV cameras you choose, purpose-built for the big screens in your NOC. These are real,
              live screens — exactly what you get.
            </p>
          </div>

          {/* Featured: CCTV wall */}
          <BrowserFrame src="/brand/business-cctv.png" label="CCTV Wall · /noc/cameras" featured />
          <div className="grid md:grid-cols-3 gap-5 mt-6 mb-16">
            <ShowcasePoint icon={<Camera className="w-5 h-5" />} title="300+ live NZTA cameras" body="Every Waka Kotahi traffic camera in the country, each still refreshing every 30 seconds." />
            <ShowcasePoint icon={<LayoutGrid className="w-5 h-5" />} title="Wall layouts 4 – 12" body="Switch from a 2×2 up to a 4×3 grid to fill any control-room television." />
            <ShowcasePoint icon={<Check className="w-5 h-5" />} title="You choose the views" body="Tick the exact cameras you want, filtered by island, region or highway." />
          </div>

          {/* Two-up: NOC overview + camera picker */}
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <BrowserFrame src="/brand/business-noc.png" label="NOC Overview · /noc" />
              <h3 className="font-display font-bold text-2xl mt-6 mb-2">Nationwide NOC overview</h3>
              <p className="text-zinc-400 leading-relaxed">
                Live severity counters, an all-of-NZ event map and a rolling event stream — auto-refreshing,
                and it never times out on a wall display. Restricted to your Admins and Business users.
              </p>
            </div>
            <div>
              <BrowserFrame src="/brand/business-cctv-select.png" label="Camera picker" />
              <h3 className="font-display font-bold text-2xl mt-6 mb-2">Pick cameras in seconds</h3>
              <p className="text-zinc-400 leading-relaxed">
                Search or filter by island, region and highway, then tick the cameras to add to the wall.
                Your selection and grid layout are remembered per screen — set up once, leave it running.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <Link to="/signup" className="btn btn-primary" data-testid="business-showcase-cta">
              Get Business <ArrowUpRight className="w-4 h-4" />
            </Link>
            <div className="text-zinc-500 text-sm mt-3">Admins &amp; Business users only · also includes freight restriction layers</div>
          </div>
        </div>
      </section>

      <section className="bg-neutral-950 py-24 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-400 mb-3">For NZ Businesses</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl mb-6 leading-tight">
              When SH1 closes,<br />
              <span className="text-orange-400">every minute counts.</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
              Logistics, fleet, and safety teams use PTT NZ Alerts to reroute drivers before delays escalate.
              Set thresholds by severity and highway, and layer in MetService weather warnings for the routes
              you run. Get notified in-app, on push, over email, or via SMS. Load pilots can schedule oversize
              loads so everyone can plan around meets, closures and bad weather.
            </p>
            <Link to="/signup" className="btn btn-secondary" data-testid="cta-band-signup">
              Start Free – NZ$0/mo
            </Link>
          </div>
          <div
            className="rounded-3xl aspect-[4/3] bg-cover bg-center border border-white/10"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1554629942-0c17bf68b884?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxOZXclMjBaZWFsYW5kJTIwaGlnaHdheSUyMHN1bnNldHxlbnwwfHx8fDE3ODMyMzUxNzN8MA&ixlib=rb-4.1.0&q=85')",
            }}
          />
        </div>
      </section>

      {/* Oversize load watch — for drivers */}
      <section className="py-20 px-6 lg:px-12" data-testid="oversize-promo-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div
            className="rounded-3xl aspect-[4/3] bg-cover bg-center border border-zinc-200 order-last lg:order-first"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1519003722824-194d4455a60c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            }}
          />
          <div>
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-orange-600 mb-3">
              <Truck className="w-4 h-4" /> For truckies &amp; drivers
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl mb-5 leading-tight">
              See the oversize load<br /><span className="text-orange-500">before you meet it.</span>
            </h2>
            <p className="text-zinc-600 text-lg mb-4 leading-relaxed">
              Nobody wants to round a bend on a narrow pass and find a house-wide load coming the other way.
              Our public Oversize Load Watch shows the planned over-dimension moves on NZ highways — routes,
              directions and timings — so you can plan your run and avoid a head-on standoff.
            </p>
            <p className="text-zinc-600 text-base mb-8 leading-relaxed">
              Free to view, no login needed. Load pilots publish their moves here, and everyone travels a little safer.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/oversize" className="btn btn-primary" data-testid="oversize-promo-view">
                View oversize loads <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/signup" className="btn btn-secondary" data-testid="oversize-promo-pilot">
                I'm a Load Pilot
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature idea form */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-teal-50 to-orange-50/40" data-testid="feature-idea-section">        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-14 h-14 rounded-2xl bg-teal-500/15 text-teal-600 flex items-center justify-center mx-auto mb-5">
              <Lightbulb className="w-7 h-7" />
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl mb-3">Got an idea for a new feature?</h2>
            <p className="text-zinc-600 text-lg">Fill out the form and we'll look at building it into the app. Tell us what would make PTT NZ - Alerts better for you.</p>
          </div>
          <FeatureIdeaForm />
        </div>
      </section>

      {/* Green energy banner */}
      <section className="bg-emerald-600 text-white" data-testid="green-energy-banner">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-center gap-2.5 text-center">
          <Leaf className="w-5 h-5 text-emerald-100 flex-shrink-0" />
          <span className="text-sm sm:text-base font-semibold">Powered 100% by solar with generator backup — keeping it clean &amp; green. 🌱</span>
        </div>
      </section>

      <footer className="py-12 px-6 lg:px-12 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-icon@2x.png" alt="PTT NZ" className="w-8 h-8 rounded-md" />
            <div>
              <div className="font-display font-black">PTT NZ Alerts</div>
              <div className="text-xs text-zinc-500">© {new Date().getFullYear()} PTT NZ Limited · Data © Waka Kotahi NZTA &amp; MetService</div>
            </div>
          </div>
          <div className="text-sm text-zinc-500 flex items-center gap-4">
            <Link to="/status" className="inline-flex items-center gap-1.5 hover:text-teal-600 transition-colors" data-testid="footer-status-link">
              <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-red" /> Feed status
            </Link>
            <span>Built with love in Aotearoa · v1.2</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureIdeaForm() {
  const [form, setForm] = useState({ name: '', email: '', description: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.description.trim().length < 5) {
      return toast.error('Please add your name, email and a short description.');
    }
    setSending(true);
    try {
      await api.post('/feedback/feature-idea', form);
      setDone(true);
      toast.success("Thanks! We've received your idea.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="card text-center py-12" data-testid="feature-idea-success">
        <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7 text-teal-600" />
        </div>
        <h3 className="font-display font-bold text-2xl mb-2">Idea received — thank you!</h3>
        <p className="text-zinc-600">We read every submission. If we need more detail, we'll email you at <strong>{form.email}</strong>.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card space-y-4" data-testid="feature-idea-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Your name</label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" data-testid="idea-name" />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="idea-email" />
        </div>
      </div>
      <div>
        <label className="label">Feature or improvement</label>
        <textarea className="input min-h-32" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the feature or improvement you'd like to see…" data-testid="idea-description" />
      </div>
      <button type="submit" className="btn btn-primary w-full sm:w-auto" disabled={sending} data-testid="idea-submit">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send idea <Send className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function StatMini({ value, label, tint }) {
  return (
    <div className="rounded-xl bg-white/10 border border-white/20 backdrop-blur-md p-4">
      <div className="font-display font-black text-3xl" style={{ color: tint }}>{value}</div>
      <div className="text-white/70 text-xs tracking-[0.15em] uppercase mt-1">{label}</div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="card card-hover" data-testid={`feature-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="w-11 h-11 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-display font-bold text-xl mb-2">{title}</h3>
      <p className="text-zinc-600 text-sm leading-relaxed">{body}</p>
    </div>
  );
}


function BrowserFrame({ src, label, featured = false }) {
  return (
    <figure className={`rounded-2xl overflow-hidden border border-white/10 bg-neutral-900 shadow-2xl ${featured ? 'ring-1 ring-teal-500/25' : ''}`}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-white/5">
        <span className="w-3 h-3 rounded-full bg-red-500/70" />
        <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
        <span className="w-3 h-3 rounded-full bg-green-500/70" />
        <span className="ml-3 text-[11px] text-zinc-400 font-mono truncate">{label}</span>
      </div>
      <img src={src} alt={label} className="w-full block" loading="lazy" />
    </figure>
  );
}

function ShowcasePoint({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <div className="w-10 h-10 rounded-lg bg-teal-500/15 text-teal-400 flex items-center justify-center mb-3">{icon}</div>
      <div className="font-bold text-white mb-1">{title}</div>
      <div className="text-sm text-zinc-400 leading-relaxed">{body}</div>
    </div>
  );
}
